import type { adminTools as ru } from '../ru/adminTools';
import { pluralEn } from '../../plural';

export const adminTools: typeof ru = {
  analytics: {
    title: 'Analytics',
    summaryTitle: 'Summary',
    emptyPeriod: 'No data for this period',
    chart: {
      zoom: 'Select range',
      zoomBack: 'Reset zoom',
      restore: 'Reset chart',
      saveImage: 'Save as PNG',
      dataView: 'Data table',
      dataViewClose: 'Close',
      dataViewRefresh: 'Refresh',
      line: 'Lines',
      bar: 'Bars',
      stack: 'Stacked',
      tiled: 'Side by side',
      showPie: 'Pie chart',
      showBars: 'Bar chart'
    },
    errors: {
      overview: 'Failed to load overview',
      retention: 'Failed to load retention data',
      heatmap: 'Failed to load activity chart',
      topPlayers: 'Failed to load top players'
    },
    sections: {
      quality: 'Match quality',
      funnel: 'Funnel',
      retention: 'New player retention',
      topPlayers: 'Most active players',
      whenPlay: 'When players play',
      modes: 'Match modes'
    },
    funnel: {
      newPlayers: 'New players',
      playedAtLeastOne: 'Played at least one match',
      conversion: 'Conversion'
    },
    heatmap: {
      weekdays: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
      tzLabel: (utc: string): string => `your time, ${utc}`,
      subtitle: (tz: string): string => `Matches by time of day (${tz})`,
      empty: 'No matches for this period',
      peak: 'Peak',
      busiest: 'Busiest',
      quietest: 'Quietest',
      byWeekday: 'By day of week',
      matches: (count: number): string => `${count} ${pluralEn(count, 'match', 'matches')}`
    },
    quality: {
      duration: (minutes: number, seconds: string): string => `${minutes}m ${seconds}s`,
      avgDuration: 'Avg duration',
      avgRound: 'Avg round',
      finished: 'Played out',
      finishedHint: 'matches completed',
      completed: 'Completed',
      aborted: 'Abandoned'
    },
    range: {
      days7: '7 days',
      days30: '30 days',
      days90: '90 days',
      custom: 'Custom',
      dateFrom: 'Start date',
      dateTo: 'End date'
    },
    retention: {
      notStarted: 'Period has not started yet',
      partial: (pct: string): string => `Partial: period has not ended yet, ${pct}%`,
      description: 'Share of players who signed up that day and came back to play later',
      signupDate: 'Sign-up date',
      newcomers: 'New players',
      nextDay: 'Returned next day',
      week: 'Within a week',
      month: 'Within a month',
      partialNote: '~ partial value, period still in progress'
    },
    summary: {
      dayLabel: 'Players per day',
      dayHint: 'unique over the last day',
      weekLabel: 'Players per week',
      weekHint: 'unique over the last 7 days',
      monthLabel: 'Players per month',
      monthHint: 'unique over the last 30 days'
    },
    online: {
      title: 'Online',
      errorLoad: 'Failed to load online data',
      players: 'Players in matches',
      site: 'Site',
      peak: 'Peak players',
      peakSite: 'Peak site',
      avgOnline: 'Average online',
      average: 'Average',
      max: 'Max',
      matches: 'Matches',
      empty: 'No online data for the selected period',
      chartLabel: 'Online chart'
    },
    timeseries: {
      title: 'Daily activity',
      newPlayers: 'New players',
      matches: 'Matches',
      activeUsers: 'Unique players'
    },
    topPlayers: {
      show: 'Show',
      player: 'Player',
      matches: 'Matches'
    },
    economy: {
      title: 'Economy',
      includeStaff: 'Include excluded',
      includeStaffHint: 'Staff (ADMIN/MODERATOR) and players from the exclusion list',
      loadError: 'Failed to load economy',
      flaggedError: 'Failed to load flagged transactions',
      journalError: 'Failed to load player journal',
      summary: {
        credited: 'Credited',
        debited: 'Debited',
        net: 'Net',
        transactions: 'Transactions',
        players: 'Players',
        flagged: 'Flagged'
      },
      daily: {
        title: 'Credited vs debited by day',
        credited: 'Credited',
        debited: 'Debited'
      },
      reasons: {
        title: 'By reason',
        reason: 'Reason',
        credited: 'Credited',
        debited: 'Debited',
        count: 'Operations',
        names: {
          MATCH_REWARD: 'Match reward',
          DAILY_REWARD: 'Daily reward',
          PROMO: 'Promo code',
          CG_SYNC: 'Game sync',
          CASE_BUY: 'Case purchase',
          CASE_DUST: 'Case dust',
          CASE_CURRENCY: 'Case currency',
          ROULETTE: 'Roulette',
          ROULETTE_BET: 'Roulette bet',
          ROULETTE_WIN: 'Roulette win',
          ROULETTE_REFUND: 'Bet refund',
          SHOP_BUY: 'Shop purchase',
          PLAYER_TIP: 'Player tip'
        } as Record<string, string>
      },
      roulette: {
        title: 'Roulette',
        bets: 'Bets',
        wins: 'Wins',
        refunds: 'Refunds',
        houseEdge: 'House edge',
        houseEdgeHint: '(bets - refunds - wins) / (bets - refunds)'
      },
      flagged: {
        title: 'Flagged transactions',
        empty: 'No flagged transactions in this period'
      },
      excluded: {
        title: 'Excluded players',
        description: 'Their operations are left out of the economy while "Include excluded" is off',
        uidLabel: 'Steam UID',
        reasonLabel: 'Reason',
        reasonPlaceholder: 'Optional',
        add: 'Exclude',
        remove: 'Restore',
        removeAria: (name: string): string => `Restore player ${name} to analytics`,
        empty: 'The exclusion list is empty',
        player: 'Player',
        reason: 'Reason',
        addedBy: 'Added by',
        date: 'When',
        loadError: 'Failed to load the exclusion list',
        addError: 'Failed to exclude the player',
        removeError: 'Failed to restore the player'
      },
      journal: {
        title: 'Player journal',
        uidLabel: 'Player Steam UID',
        uidPlaceholder: 'e.g. 76561198000000000',
        show: 'Show',
        hint: 'Enter a Steam UID to see the player operations',
        empty: 'The player has no transactions'
      },
      table: {
        player: 'Player',
        amount: 'Amount',
        balanceAfter: 'Balance after',
        reason: 'Reason',
        reference: 'Reference',
        date: 'Date',
        flaggedBadge: 'flagged',
        openPlayer: (name: string): string => `Open player ${name}`
      },
      pager: {
        prev: 'Prev',
        next: 'Next',
        info: (page: number, total: number, count: number): string => `Page ${page} of ${total} · ${count} total`
      }
    }
  },
  matchSettings: {
    badgeOn: 'On',
    badgeOff: 'Off',
    dialogKicker: 'Match settings',
    createTitle: 'Create setting',
    editTitle: 'Edit setting',
    kicker: 'Configuration',
    title: 'Match settings',
    createButton: '+ Create',
    loadFailedTitle: 'Failed to load settings',
    emptyTitle: 'No settings yet',
    emptyDescription: 'Create your first match setting. CG receives the active settings when a match starts.',
    form: {
      name: 'Name',
      namePlaceholder: 'E.g. ranked_1v1',
      active: 'Active',
      enabledHint: 'Enabled: CG receives it at match start',
      disabledHint: 'Disabled: not sent to CG',
      creating: 'Creating…',
      saving: 'Saving…'
    },
    deleteDialog: {
      title: 'Delete setting?',
      prefix: 'Setting ',
      suffix: ' will be permanently deleted. CG will stop receiving it at match start.',
      deleting: 'Deleting…'
    },
    errors: {
      load: 'Failed to load match settings.',
      nameRequired: 'Enter a setting name.',
      configNotObject: 'Config must be a JSON object (not an array or a primitive).',
      invalidJson: 'Invalid JSON in the Config field. Check the syntax.',
      save: 'Failed to save the setting.',
      delete: 'Failed to delete the setting.'
    },
    toasts: {
      created: 'Setting created.',
      saved: 'Setting saved.',
      deleted: 'Setting deleted.'
    },
    table: {
      status: 'Status',
      updated: 'Updated',
      actions: 'Actions',
      edit: 'Edit',
      editAria: (name: string): string => `Edit ${name}`,
      deleteAria: (name: string): string => `Delete ${name}`
    }
  },
  promoCodes: {
    title: 'Promo codes',
    kicker: 'Administration',
    createTitle: 'Create promo code',
    createButton: '+ Create',
    deactivate: 'Deactivate',
    noExpiry: 'No expiry',
    paymentAuthor: 'Payment',
    loadFailedTitle: 'Failed to load promo codes',
    emptyTitle: 'No promo codes yet',
    emptyDescription: 'Create your first promo code. Players will be able to redeem it in the client.',
    status: {
      active: 'Active',
      inactive: 'Deactivated'
    },
    uses: {
      infinite: '∞ Unlimited',
      infiniteAria: 'Unlimited',
      exhausted: 'Exhausted',
      exhaustedAria: 'Exhausted',
      remainingAria: (remaining: number): string => `${remaining} left`
    },
    form: {
      code: 'Code',
      codePlaceholder: 'E.g. SUMMER2026',
      untilDate: 'Expiry date',
      untilDateHint: '(optional: empty = no expiry)',
      remainingUses: 'Number of activations',
      remainingUsesHint: '−1 = unlimited, 0 = disable, N > 0 = remaining',
      balance: 'Balance',
      balanceHint: 'Amount credited to the player when the promo code is activated. Integer ≥ 0.',
      creating: 'Creating…'
    },
    deactivateDialog: {
      title: 'Deactivate promo code?',
      prefix: 'Promo code ',
      suffix:
        ' will no longer be available for activation. This cannot be undone: the code cannot be re-enabled. Activations already made are not reverted.',
      deactivating: 'Deactivating…'
    },
    activationsDialog: {
      titlePrefix: 'Activations of ',
      loadFailedTitle: 'Failed to load activations',
      emptyTitle: 'No activations yet',
      emptyDescription: 'Nobody has activated this code yet.',
      next: 'Next',
      pageSummary: (page: number, totalPages: number, total: number): string =>
        `Page ${page} / ${totalPages}, ${total} total`
    },
    errors: {
      load: 'Failed to load promo codes.',
      loadActivations: 'Failed to load activations.',
      codeRequired: 'Enter a promo code.',
      codeTooLong: 'The code cannot be longer than 64 characters.',
      usesInvalid: 'Number of activations must be an integer ≥ −1 (−1 = unlimited).',
      balanceInvalid: 'Balance must be an integer ≥ 0.',
      duplicate: 'A promo code with this code already exists.',
      create: 'Failed to create the promo code.',
      deactivate: 'Failed to deactivate the promo code.'
    },
    toasts: {
      created: 'Promo code created.',
      deactivated: 'Promo code deactivated.'
    },
    table: {
      author: 'Author',
      status: 'Status',
      expiry: 'Expires',
      activations: 'Activations',
      activated: 'Activated',
      remaining: 'Remaining',
      created: 'Created',
      actions: 'Actions',
      activationsAria: (code: string): string => `Activations of promo code ${code}`,
      deactivateAria: (code: string): string => `Deactivate promo code ${code}`
    }
  },
  dedicatedKeys: {
    title: 'Server keys',
    kicker: 'Administration',
    subtitle:
      'V2/V3 key pairs for Dota dedicated servers. Raw values are not shown after saving, only the name and the date added.',
    addTitle: 'Add key',
    addButton: '+ Add',
    loadFailedTitle: 'Failed to load keys',
    emptyTitle: 'No keys yet',
    emptyDescription: 'Add your first V2/V3 key pair for a dedicated server.',
    form: {
      label: 'Name',
      labelPlaceholder: 'E.g. host-1',
      keyV2: 'Key V2',
      keyV3: 'Key V3',
      keysHint: 'After saving, the keys themselves are never shown again, only the name and creation date.',
      adding: 'Adding…'
    },
    deleteDialog: {
      title: 'Delete key?',
      prefix: 'Key ',
      suffix: ' will be permanently deleted. Servers using this pair will lose access.',
      deleting: 'Deleting…'
    },
    errors: {
      load: 'Failed to load server keys.',
      labelRequired: 'Enter a key name.',
      keysRequired: 'Enter both values: key V2 and key V3.',
      duplicate: 'This V2 + V3 key pair has already been added.',
      add: 'Failed to add the key.',
      delete: 'Failed to delete the key.'
    },
    toasts: {
      added: 'Key added.',
      deleted: 'Key deleted.'
    },
    table: {
      created: 'Created',
      actions: 'Actions',
      deleteAria: (label: string): string => `Delete key ${label}`
    }
  },
  polls: {
    title: 'Polls',
    kicker: 'Administration',
    createButton: '+ Create',
    createTitle: 'New poll',
    editTitle: 'Edit poll',
    detailsTitle: 'Poll statistics',
    loadFailedTitle: 'Failed to load polls',
    emptyTitle: 'No polls yet',
    emptyDescription: 'Create the first poll. The active poll is shown to players on the loading screen.',
    status: {
      active: 'Active',
      inactive: 'Inactive'
    },
    table: {
      question: 'Question',
      status: 'Status',
      created: 'Created',
      activated: 'Activated',
      deactivated: 'Deactivated',
      votes: 'Votes',
      actions: 'Actions',
      none: '—'
    },
    actions: {
      details: 'Statistics',
      edit: 'Edit',
      activate: 'Activate',
      deactivate: 'Deactivate',
      detailsAria: (question: string): string => `Poll statistics: ${question}`,
      editAria: (question: string): string => `Edit poll: ${question}`,
      activateAria: (question: string): string => `Activate poll: ${question}`,
      deactivateAria: (question: string): string => `Deactivate poll: ${question}`
    },
    form: {
      questionRu: 'Question (RU)',
      questionEn: 'Question (EN)',
      optionsTitle: 'Answer options',
      optionsHint: '2 to 10 options, shown in list order. Both languages are required.',
      optionRu: (n: number): string => `Option ${n} (RU)`,
      optionEn: (n: number): string => `Option ${n} (EN)`,
      addOption: '+ Add option',
      removeOption: 'Remove',
      removeOptionAria: (n: number): string => `Remove option ${n}`,
      removeBlocked: 'Cannot remove: this option already has votes',
      moveUpAria: (n: number): string => `Move option ${n} up`,
      moveDownAria: (n: number): string => `Move option ${n} down`,
      votesBadge: (count: number): string => `Votes: ${count}`,
      save: 'Save',
      create: 'Create',
      saving: 'Saving…'
    },
    errors: {
      questionRequired: 'Fill in the question in Russian and English',
      optionsCount: 'Between 2 and 10 options are required',
      optionTextRequired: 'Fill in every option in Russian and English',
      loadDetails: 'Failed to load the poll',
      create: 'Failed to create the poll',
      update: 'Failed to save the poll',
      activate: 'Failed to activate the poll',
      deactivate: 'Failed to deactivate the poll',
      conflict: 'Cannot remove an option that already has votes'
    },
    toasts: {
      created: 'Poll created (inactive)',
      updated: 'Poll saved',
      activated: 'Poll activated',
      deactivated: 'Poll deactivated'
    },
    activateDialog: {
      title: 'Activate this poll?',
      text: 'The poll becomes active and is shown to players on the loading screen.',
      replaceWarning: 'The currently active poll will be deactivated.',
      confirming: 'Activating…'
    },
    deactivateDialog: {
      title: 'Deactivate this poll?',
      text: 'The poll will no longer be shown to players. Statistics are kept.',
      confirming: 'Deactivating…'
    },
    stats: {
      totalVotes: 'Total votes',
      optionsTitle: 'Results by option',
      votesAria: (text: string, votes: number, percent: string): string => `${text}: ${votes} votes, ${percent}`,
      noVotes: 'Nobody has voted yet'
    }
  }
};
